import { useCallback } from 'react';
import { IAccount } from './account';

import useRequestJSON, {
  IsLoadingSegment,
  RunnerSegment
} from './hooks/useRequestJSON';
import {
  PostJSON,
  ErrorResponse,
  BaseResponse,
  IDParams,
  PaginatedParams
} from './request';
import { GetTagsByValue } from './tag';
import { AfterHoursMessages } from '../components/organisms/contact/types';
import type { ITask } from './tasks';

export enum NotificationPreference {
  Call = 'C',
  Page = 'P',
  Email = 'E',
  Integration = 'I',
  Off = 'O'
}

export enum ChatNotificationPreference {
  Off = 'O',
  Email = 'E'
}

export enum DigestFrequency {
  Off = 'O',
  Daily = 'D',
  Tridaily = 'T',
  Weekly = 'W',
  Biweekly = 'B'
}

export const NotificationMapping = {
  [NotificationPreference.Call]: 'Phone Call',
  [NotificationPreference.Page]: 'Page',
  [NotificationPreference.Email]: 'Email',
  [NotificationPreference.Integration]: 'Integration',
  [NotificationPreference.Off]: 'Off'
};

export const ChatNotificationMapping = {
  [NotificationPreference.Email]: 'Email',
  [NotificationPreference.Off]: 'Off'
};

export interface ContactCC {
  id?: number;
  email?: string;
  isVoided?: boolean;
  voidedAt?: Date;
  contactId?: number;
  contact?: IContact;
  copyId?: number;
  copy?: IContact;
}

export interface IContact {
  id: number;
  displayName: string;
  description?: string;
  email?: string;
  primaryPhone: string;
  primaryExtension?: string;
  primaryStartHour?: number;
  primaryStartMinute?: number;
  primaryEndHour?: number;
  primaryEndMinute?: number;
  primaryDays: string;
  offHourPhone?: string;
  offHourExtension?: string;
  afterHoursMessages?: AfterHoursMessages;
  alertPhone?: string;
  alertExtension?: string;
  alertStartHour?: number;
  alertStartMinute?: number;
  alertEndHour?: number;
  alertEndMinute?: number;
  alertDays?: string;
  notificationPreference: NotificationPreference;
  chatNotificationPreference: ChatNotificationPreference;
  receiveReports?: boolean;
  reportDigestFrequency?: DigestFrequency;
  alertDigestFrequency?: DigestFrequency;
  chatDigestFrequency?: DigestFrequency;
  backupSetAt?: Date;
  backupId?: number;
  vacation?: string;
  isUsingBackup?: boolean; // Computed server-side
  tz: string;
  npi?: string;
  tags?: string;
  ccs?: ContactCC[];
  isDeleted?: boolean;
  accountId: number;
}

export interface IReferralContact {
  id: number;
  name: string;
  phone: string;
  extension?: string;
  isDeleted?: string;
  address?: string;
  tags?: string;
  uninsured?: boolean;
  medicare?: boolean;
  medicaid?: boolean;
  ownerId: number;
  owner?: IAccount;
  createdById: number;
  createdBy?: IAccount;
  updatedById: number;
  updatedBy?: IAccount;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
}

export interface IContactGroup {
  id: number;
  name: string;
  description: string;
  accountId: number;
}

export interface IContactGroupMember {
  id: number;
  contactGroupId: number;
  isVoided: boolean;
  contact: IContact;
}

// Utils

export function FormatContact(
  contact: IContact,
  includeEmail?: boolean
): string {
  let name = contact.displayName;
  if (contact.description && contact.description.trim().length > 0) {
    name = `${name} (${contact.description})`;
  }
  if (includeEmail && contact.email) {
    name += ' (' + contact.email + ')';
  }
  return name;
}

export interface GetContactParams {
  id?: number;
  ids?: number[];
  lite?: boolean;
}

export interface GetContactListParams {
  includeDeleted?: boolean;
}

export interface GetContactsParams extends PaginatedParams {
  keyword?: string;
  excludeIds?: number[];
  lite?: boolean;
}

export interface GetReferralContactsParams extends PaginatedParams {
  uninsured?: boolean;
  medicare?: boolean;
  medicaid?: boolean;
  name?: string;
  phone?: string;
  tags?: string;
  isCreator?: boolean;
}

export interface GetContactResponse extends BaseResponse {
  contact?: IContact;
  contacts?: IContact[];
  vacationTasks?: ITask[];
}

export interface GetContactListResponse extends BaseResponse {
  contacts: IContact[];
}

export interface GetContactsResponse extends BaseResponse {
  contacts: IContact[];
  total: number;
}

export interface ReferralContactResponse {
  status: 'DUPLICATE' | 'FAIL' | 'OK';
  message?: string;
  referralContact?: IReferralContact;
  referralContacts?: IReferralContact[];
}

export interface GetReferralContactsResponse extends BaseResponse {
  referralContacts: IReferralContact[];
  total: number;
}

export interface GetContactGroupsParams extends PaginatedParams {
  keyword?: string;
}

export interface GetContactGroupsResponse extends BaseResponse {
  contactGroups: IContactGroup[];
  total: number;
}

export interface GetSingleContactGroupResponse extends BaseResponse {
  group: IContactGroup;
  members: IContactGroupMember[];
}

interface GetContactFn {
  (params: GetContactParams): Promise<GetContactResponse | ErrorResponse>;
}

interface GetContactListFn {
  (
    params: GetContactListParams
  ): Promise<GetContactListResponse | ErrorResponse>;
}

interface GetContactsFn {
  (params: GetContactsParams): Promise<GetContactsResponse | ErrorResponse>;
}

interface GetReferralContactsFn {
  (
    params: GetReferralContactsParams
  ): Promise<GetReferralContactsResponse | ErrorResponse>;
}

interface GetContactGroupsFn {
  (params: PaginatedParams): Promise<GetContactGroupsResponse | ErrorResponse>;
}

interface GetSingleContactGroupFn {
  (params: IDParams): Promise<GetSingleContactGroupResponse | ErrorResponse>;
}

export const GetContact: GetContactFn = (params) =>
  PostJSON<GetContactResponse, GetContactParams>({
    url: '/contact/data_single/',
    params
  });

export const GetContacts: GetContactsFn = (params) =>
  PostJSON<GetContactsResponse, GetContactsParams>({
    url: '/contact/data/',
    params
  });

export const GetContactList: GetContactListFn = (params) =>
  PostJSON<GetContactListResponse, GetContactListParams>({
    url: '/contact/list/',
    params
  });

export const GetReferralContacts: GetReferralContactsFn = (params) =>
  PostJSON<GetReferralContactsResponse, GetReferralContactsParams>({
    url: '/referral/data_contacts/',
    params: {
      ...params,
      isCreator: true
    }
  });

export const GetContactGroups: GetContactGroupsFn = (params) =>
  PostJSON<GetContactGroupsResponse, GetContactGroupsParams>({
    url: '/contact/data_contact_groups/',
    params: {
      ...params
    }
  });

export const GetSingleContactGroup: GetSingleContactGroupFn = (params) =>
  PostJSON<GetSingleContactGroupResponse, IDParams>({
    url: '/contact/data_contact_group_single/',
    params
  });

export const transformContact = (
  response: GetContactResponse
): Omit<GetContactResponse, 'status'> => {
  return {
    contacts: response.contacts,
    contact: response.contact,
    vacationTasks: response.vacationTasks
  };
};

export const transformContactList = (
  response: GetContactListResponse
): Omit<GetContactListResponse, 'status'> => {
  return {
    contacts: response.contacts
  };
};

export const transformContacts = (
  response: GetContactsResponse
): Omit<GetContactsResponse, 'status'> => {
  return {
    contacts: response.contacts,
    total: response.total
  };
};

export const transformReferralContacts = (
  response: GetReferralContactsResponse
): Omit<GetReferralContactsResponse, 'status'> => {
  return {
    referralContacts: response.referralContacts,
    total: response.total
  };
};

// Hooks

export const useGetContact = (
  params: GetContactParams
): Omit<GetContactResponse, 'status'> & RunnerSegment => {
  const request = useCallback(
    () => GetContact(params),
    [params.id, params.ids]
  );
  const { data, run } = useRequestJSON<
    GetContactResponse,
    Omit<GetContactResponse, 'status'>
  >({
    request,
    enabled: params.id != null || (params.ids && params.ids.length > 0),
    transformFn: transformContact
  });
  return {
    contact: data && data.contact ? data.contact : undefined,
    contacts: data && data.contacts ? data.contacts : undefined,
    vacationTasks: data && data.vacationTasks ? data.vacationTasks : undefined,
    run: () => run(request)
  };
};

export const useGetContactsByID = useGetContact; // alias so not to cause confusion

export const useGetContactList = (
  params?: GetContactListParams
): Omit<GetContactListResponse, 'status'> & RunnerSegment => {
  params = params || {
    includeDeleted: false
  };
  const request = useCallback(
    () => GetContactList(params),
    [params.includeDeleted]
  );
  const { data, run } = useRequestJSON<
    GetContactListResponse,
    Omit<GetContactListResponse, 'status'>
  >({
    request,
    transformFn: transformContactList
  });
  return {
    contacts: data ? data.contacts : undefined,
    run: () => run(request)
  };
};

export const useGetContacts = (
  params?: GetContactsParams,
  isEnabled = true
): Omit<GetContactsResponse, 'status'> & RunnerSegment & IsLoadingSegment => {
  params = params || {
    page: 0,
    limit: 10
  };
  /**
   * The dependency array for useCallback should
   * only contain primitive values. React only does
   * shallow comparisons, so objects and arrays will
   * never equal each other, which in this case
   * causes "GetContacts" to be called too frequently.
   *
   * The solution below converts the excludeIds array
   * into a string, thus converting it into a primitive.
   */
  let excludeIds = '';
  if (params.excludeIds && params.excludeIds.length > 0) {
    excludeIds = params.excludeIds.join(',');
  }
  const request = useCallback(
    () => GetContacts(params),
    [params.page, params.limit, params.keyword, excludeIds]
  );
  const { data, run, isLoading } = useRequestJSON<
    GetContactsResponse,
    Omit<GetContactsResponse, 'status'>
  >({
    request,
    transformFn: transformContacts,
    enabled: isEnabled
  });
  return {
    contacts: data ? data.contacts : undefined,
    total: data ? data.total : undefined,
    isLoading,
    run: () => run(request)
  };
};

export const useGetReferralContacts = (
  params?: GetReferralContactsParams
): Omit<GetReferralContactsResponse, 'status'> & RunnerSegment => {
  params = params || {
    page: 0,
    limit: 5
  };
  const request = useCallback(
    () => GetReferralContacts(params),
    [
      params.page,
      params.limit,
      params.isCreator,
      params.medicaid,
      params.medicare,
      params.uninsured,
      params.name,
      params.phone,
      params.tags
    ]
  );
  const { data, run } = useRequestJSON<
    GetReferralContactsResponse,
    Omit<GetReferralContactsResponse, 'status'>
  >({
    request,
    transformFn: transformReferralContacts
  });
  return {
    referralContacts: data ? data.referralContacts : undefined,
    total: data ? data.total : undefined,
    run: () => run(request)
  };
};

export const useGetContactGroups = (
  params?: GetContactGroupsParams
): Omit<GetContactGroupsResponse, 'status'> & RunnerSegment => {
  params = params || {
    page: 0,
    limit: 5
  };
  const request = useCallback(
    () => GetContactGroups(params),
    [params.page, params.limit, params.keyword]
  );
  const { data, run } = useRequestJSON<
    GetContactGroupsResponse,
    Omit<GetContactGroupsResponse, 'status'>
  >({
    request,
    transformFn: (response) => {
      return {
        contactGroups: response.contactGroups,
        total: response.total
      };
    }
  });
  return {
    contactGroups: data ? data.contactGroups : undefined,
    total: data ? data.total : undefined,
    run: () => run(request)
  };
};

// Request functions

export type ContactParams = Pick<
  IContact,
  | 'id'
  | 'displayName'
  | 'description'
  | 'email'
  | 'primaryPhone'
  | 'primaryExtension'
  | 'primaryStartHour'
  | 'primaryStartMinute'
  | 'primaryEndHour'
  | 'primaryEndMinute'
  | 'primaryDays'
  | 'offHourPhone'
  | 'offHourExtension'
  | 'afterHoursMessages'
  | 'alertPhone'
  | 'alertExtension'
  | 'alertStartHour'
  | 'alertStartMinute'
  | 'alertEndHour'
  | 'alertEndMinute'
  | 'alertDays'
  | 'notificationPreference'
  | 'chatNotificationPreference'
  | 'receiveReports'
  | 'reportDigestFrequency'
  | 'alertDigestFrequency'
  | 'chatDigestFrequency'
  | 'backupId'
  | 'tz'
  | 'npi'
  | 'tags'
  | 'ccs'
> & {
  vacationStartDate?: string;
  vacationEndDate?: string;
  cancelVacation?: boolean;
};

export interface ContactResponse extends BaseResponse {
  contact: IContact;
}

export const CreateContact = (
  params: Omit<ContactParams, 'id'>
): Promise<ContactResponse | ErrorResponse> => {
  // Exchange the tags with IDs
  return GetTagsByValue(params.tags).then((tags) => {
    const _params = {
      ...params,
      tags: tags.map((tag) => tag.id).join(',')
    };
    // Create the contact
    return PostJSON({
      url: '/contact/create/',
      params: _params
    });
  });
};

export const EditContact = (
  params: Partial<ContactParams> & IDParams
): Promise<ContactResponse | ErrorResponse> => {
  // Exchange the tags with IDs
  return GetTagsByValue(params.tags).then((tags) => {
    const _params = {
      ...params,
      tags: tags.map((tag) => tag.id).join(',')
    };
    // Edit the contact
    return PostJSON<ContactResponse>({
      url: '/contact/edit/',
      params: _params
    });
  });
};

export interface ReferralContactParams {
  id?: number;
  name: string;
  phone: string;
  address?: string;
}

export const CreateReferralContact = (
  params: ReferralContactParams
): Promise<ReferralContactResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/referral/create_contact/',
    params: params
  });
};

export const EditReferralContact = (
  params: ReferralContactParams
): Promise<ReferralContactResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/referral/edit_contact/',
    params: params
  });
};

export type RemoveContactParams = IDParams;

export const RemoveContact = (
  params: RemoveContactParams
): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/contact/remove/',
    params: params
  });
};

export const RemoveReferralContact = (
  params: RemoveContactParams
): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/referral/remove_contact/',
    params: params
  });
};
